import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle2, Loader2, AlertCircle, ArrowRight, Home } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking');
  const [paymentData, setPaymentData] = useState(null);
  const [pollAttempts, setPollAttempts] = useState(0);
  
  const sessionId = searchParams.get('session_id');
  
  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }
    
    pollPaymentStatus();
  }, [sessionId]);
  
  const pollPaymentStatus = async () => {
    const maxAttempts = 5;
    const pollInterval = 2000;
    
    if (pollAttempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }
    
    try {
      const response = await fetch(`${API}/api/payments/checkout/status/${sessionId}`);
      const data = await response.json();
      
      if (data.payment_status === 'paid') {
        setStatus('success');
        setPaymentData(data);
        return;
      } else if (data.status === 'expired') {
        setStatus('expired');
        return;
      }
      
      // Continue polling if still pending
      setPollAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, pollInterval);
      
    } catch (error) {
      console.error('Error checking payment status:', error);
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-dark-navy flex items-center justify-center p-6">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="glass-card p-8 max-w-md w-full text-center"
      >
        {status === 'checking' && (
          <>
            <Loader2 className="w-16 h-16 text-neon-green mx-auto mb-6 animate-spin" />
            <h1 className="text-2xl font-heading font-bold text-white mb-2">
              Processing Payment
            </h1>
            <p className="text-slate-400">
              Please wait while we confirm your payment...
            </p>
          </>
        )}
        
        {status === 'success' && (
          <>
            <div className="w-20 h-20 rounded-full bg-neon-green/20 flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-10 h-10 text-neon-green" />
            </div>
            <h1 className="text-2xl font-heading font-bold text-white mb-2">
              Payment Successful!
            </h1>
            <p className="text-slate-400 mb-6">
              Thank you for your purchase. We've sent a confirmation email with your order details.
            </p>
            
            {paymentData && (
              <div className="bg-white/5 rounded-lg p-4 mb-6 text-left">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-slate-400">Amount Paid:</span>
                  <span className="text-white font-medium">
                    ${(paymentData.amount_total / 100).toFixed(2)} {paymentData.currency?.toUpperCase()}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">Transaction ID:</span>
                  <span className="text-neon-cyan font-mono text-xs">
                    {sessionId?.substring(0, 20)}...
                  </span>
                </div>
              </div>
            )}
            
            <div className="flex flex-col sm:flex-row gap-3">
              <Link to="/" className="flex-1 btn-secondary justify-center">
                <Home className="w-4 h-4 mr-2" />
                Home
              </Link>
              <Link to="/portal/login" className="flex-1 btn-primary justify-center">
                Go to Portal
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </div>
          </>
        )}
        
        {(status === 'error' || status === 'expired' || status === 'timeout') && (
          <>
            <div className="w-20 h-20 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-6">
              <AlertCircle className="w-10 h-10 text-red-400" />
            </div>
            <h1 className="text-2xl font-heading font-bold text-white mb-2">
              {status === 'timeout' ? 'Payment Verification Timeout' : 'Payment Issue'}
            </h1>
            <p className="text-slate-400 mb-6">
              {status === 'timeout' 
                ? 'We couldn\'t verify your payment in time. Please check your email for confirmation.'
                : status === 'expired'
                  ? 'Your payment session has expired. Please try again.'
                  : 'There was an issue processing your payment. Please contact support if you were charged.'
              }
            </p>
            
            <div className="flex flex-col sm:flex-row gap-3">
              <Link to="/" className="flex-1 btn-secondary justify-center">
                <Home className="w-4 h-4 mr-2" />
                Home
              </Link>
              <Link to="/cameras" className="flex-1 btn-primary justify-center">
                Try Again
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </div>
          </>
        )}
      </motion.div>
    </div>
  );
};

export const PaymentCancel = () => {
  return (
    <div className="min-h-screen bg-dark-navy flex items-center justify-center p-6">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="glass-card p-8 max-w-md w-full text-center"
      >
        <div className="w-20 h-20 rounded-full bg-yellow-500/20 flex items-center justify-center mx-auto mb-6">
          <AlertCircle className="w-10 h-10 text-yellow-400" />
        </div>
        <h1 className="text-2xl font-heading font-bold text-white mb-2">
          Payment Cancelled
        </h1>
        <p className="text-slate-400 mb-6">
          Your payment was cancelled. No charges were made to your account.
        </p>
        
        <div className="flex flex-col sm:flex-row gap-3">
          <Link to="/" className="flex-1 btn-secondary justify-center">
            <Home className="w-4 h-4 mr-2" />
            Home
          </Link>
          <Link to="/cameras" className="flex-1 btn-primary justify-center">
            Return to Cameras
            <ArrowRight className="w-4 h-4 ml-2" />
          </Link>
        </div>
      </motion.div>
    </div>
  );
};

export default PaymentSuccess;
